<template>
  <aha-modal
    v-if="isShowingModal"
    @hide="hide()"
    :title="titleModal"
  >
    <div v-if="canUseMultipleSessions">
      {{ $i18nPresenter.t("Resetting will clear all votes, reactions, and responses, but don’t worry — your previous results are safe in the report.") }}
    </div>

    <div v-else>
      <p
        v-if="!$store.state.presentation.hasExampleResponses || defaultModal"
      >
        {{ $i18nPresenter.t("You are about to delete all ") }}
        <u v-tooltip="resultsTooltip" class="cursor-pointer">{{ $i18nPresenter.t("results") }}</u>
        {{ $i18nPresenter.t("from participants ") }} <b>{{ $i18nPresenter.t("in this presentation") }}</b>.
        <span class="block pt-2">
          <aha-icon
            name="shield"
            maintainColor
            svgMaintainColor
            class="mb-1"
          />
          {{ $i18nPresenter.t("Your own questions and content will not be removed.") }}</span>
      </p>
      <p v-else>
        <span>
          {{ $i18nPresenter.t("Remove all example votes, submissions and reactions from this template?") }}
        </span>
        <span class="block pt-2">{{ $i18nPresenter.t("The questions and content will not be removed.") }}</span>
      </p>
    </div>
    <template #action>
      <a
        class="learn-more-link"
        href="https://help.ahaslides.com/portal/en/kb/articles/resetting-the-results"
        target="_blank"
        v-if="canUseMultipleSessions"
      >
        {{ $i18nPresenter.t('Learn more') }}
        <i class="fas fa-external-link-alt ml-2"></i>
      </a>
      <aha-antd-button
        v-aha-emit-action
        name="confirm-reset-results-button"
        class="mr-2"
        variant="danger"
        isLoadingInline
        :disabled="isClearingData"
        @click="handleClearData()"
        :submitting="isClearingData"
        v-if="!canUseMultipleSessions"
      >
        <span :class="{ 'pl-1.5': isClearingData }"> {{ $i18nPresenter.t("Reset") }}</span>
      </aha-antd-button>
      <aha-antd-button
        v-aha-emit-action
        name="cancel-reset-results-button"
        variant="secondary"
        :disabled="isClearingData"
        @click="hide()"
      >
        <span class="font-bold">{{ $i18nPresenter.t("Cancel") }}</span>
      </aha-antd-button>
      <aha-antd-button
        v-aha-emit-action
        name="confirm-reset-results-button"
        class="ml-2"
        variant="primary"
        isLoadingInline
        :disabled="isClearingData"
        @click="handleClearData()"
        :submitting="isClearingData"
        v-if="canUseMultipleSessions"
      >
        <span :class="{ 'pl-1.5': isClearingData }"> {{ $i18nPresenter.t("Reset results") }}</span>
      </aha-antd-button>
    </template>
  </aha-modal>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import PresentationApi from '@/api/presentation'
import Utils from '@/util'
import constants from '@/constant'
import VueToastedUtils from '@/utils/vue-toasted.utils.js'
import eventCategories from '@/constant/eventCategories'
import EventBus from '@/eventBus'

export default {
  props: {
    presentation: {
      type: Object,
      default: () => {},
    },
    clearDataHandler: {
      type: Function,
      default: null,
    },
    title: {
      type: String,
      default: 'Clear data confirmation',
    },
    defaultModal: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isClearingData: false,
      isShowingModal: false,
    }
  },
  computed: {
    ...mapState({
      folders: (state) => state.folders,
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters('multipleSessions', ['canUseMultipleSessions']),
    resultsTooltip() {
      return this.$i18nPresenter.t('<p>Results include all participants’ <b> votes</b>, <b>submissions</b> and <b>reactions</b>.</p>')
    },
    titleModal() {
      if (this.canUseMultipleSessions) {
        return this.$i18nPresenter.t('Reset results for entire presentation')
      }
      return this.title
    },
  },
  mounted() {
    this.isShowingModal = true
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction', 'setPresentationsAction', 'getPresentationByIdAction', 'setSlideActiveAction']),
    ...mapActions('presentationReport', ['clearPresentationReportAction']),
    ...mapActions('presentationChat', ['clearChatMessagesAction']),
    ...mapActions(['patchPresentationAction']),
    ...mapActions('multipleSessions', ['publishNewSessionAction']),
    trackEvent(label, value) {
      Utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_LIST,
        label,
        value,
      )
    },

    handleReleaseNewSession() {
      this.isClearingData = false
      this.hide()
      VueToastedUtils.showToast(this.$i18nPresenter.t('All set! Your presentation has been reset.'), {}, {
        hasReturnValue: true,
        duration: 8000,
        action: {
          text: this.$i18nPresenter.t('View saved sessions'),
          class: 'custom-toast__loading-url',
          onClick: () => {
            this.$router.push({
              name: 'PresentationReport',
              params: { id: this.presentation.id },
            })
            this.isShowReport = true
            Utils.trackGA4AndMixpanel(eventCategories.PRESENTATION_EDITOR, {
              eventAction: 'Editor > Result results toaster',
              eventCategory: eventCategories.PRESENTATION_EDITOR,
              ...{
                presentationId: this.presentation.id,
              },
            })
          },
        },
      })
    },

    async createNewPresentationSession() {
      const presentationId = this.presentation.id
      try {
        await this.publishNewSessionAction(presentationId)
        await this.setSlideActiveAction({})

        EventBus.$emit('getDataPresentationDetail', () => {
          // handle call back after get presentation detail
          this.handleReleaseNewSession()
        }, 'ClearData.vue@createNewPresentationSession')
        const newPresentations = this.$store.state.presentations
          .map((p) => {
            if (this.presentation.id === p.id) {
              return {
                ...p,
                participantsCount: 0,
                onlineCount: 0,
                numberOfHearts: 0,
                numberOfLikes: 0,
                numberOfLaughs: 0,
                numberOfSads: 0,
                numberOfWows: 0,
                exportData: {
                  ...this.presentation.exportData,
                  reportUrl: null,
                  reportStatus: null,
                  reportUpdatedAt: null,
                },
              }
            }

            return p
          })

        this.setPresentationsAction(newPresentations)
        if (this.source === 'presentation-list') {
          this.handleReleaseNewSession()
        }
      } catch (error) {
        this.setErrorOnInternalServerAction(`Cannot erase data on presentation: ${this.presentation.name}`)
        this.trackEvent(constants.eventCategories.PRESENTATION_LIST, 'Presentation List > Clear data session > Failed', this.presentation.id)
        throw error
      }
    },

    async handleClearData() {
      this.isClearingData = true

      if (this.canUseMultipleSessions) {
        this.createNewPresentationSession()
        return
      }

      if (this.clearDataHandler) {
        this.clearDataHandler()
      } else {
        try {
          await PresentationApi.clearPresentationData({
            id: this.presentation.id,
          })
          this.patchPresentationAction({
            hasResults: false,
          })
          Utils.showToastSuccess(this.$i18nPresenter.t('Reset all results'))

          // reset participants count to the state
          const newPresentations = this.$store.state.presentations
            .map((p) => {
              if (this.presentation.id === p.id) {
                return {
                  ...p,
                  participantsCount: 0,
                  onlineCount: 0,
                  numberOfHearts: 0,
                  numberOfLikes: 0,
                  numberOfLaughs: 0,
                  numberOfSads: 0,
                  numberOfWows: 0,
                  exportData: {
                    ...this.presentation.exportData,
                    reportUrl: null,
                    reportStatus: null,
                    reportUpdatedAt: null,
                  },
                }
              }

              return p
            })

          this.setPresentationsAction(newPresentations)
          this.clearPresentationReportAction()
          this.trackEvent(constants.eventCategories.PRESENTATION_LIST, 'Presentation List > Clear data > Succeed', this.presentation.id)
        } catch (error) {
          this.setErrorOnInternalServerAction(`Cannot erase data on presentation: ${this.presentation.name}`)
          this.trackEvent(constants.eventCategories.PRESENTATION_LIST, 'Presentation List > Clear data > Failed', this.presentation.id)
          throw error
        } finally {
          this.isClearingData = false
          this.hide()
        }
      }
      this.clearChatMessagesAction({
        uniqueAccessCode: this.presentation.uniqueAccessCode,
      })
    },
    hide() {
      this.isShowingModal = false
      setTimeout(() => {
        this.$emit('hide')
      }, 150)
    },
  },
}
</script>

<style lang="scss" scoped>
.learn-more-link {
  display: block;
  width: 100%;
  text-align: left;
  color: #6A1EBB;
}
</style>
